/**
 * Generate a consistent index from a string ID
 * Uses a simple hash function to ensure the same ID always produces the same result
 */
function hashStringToNumber(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

/**
 * Vintage train ticket color palettes inspired by old railway tickets
 * Each palette represents a different historic railway company aesthetic
 */
const vintageTicketPalettes = [
  // Burgundy/Maroon - Classic Pullman style
  {
    background: '#8B2942',
    backgroundLight: '#A43D56',
    border: '#5C1A2D',
    text: '#FFF8E7',
    textMuted: '#E8D5C4',
    accent: '#D4A574',
    stripe: '#6B2237',
  },
  // Forest Green - Great Northern Railway
  {
    background: '#2D5A4A',
    backgroundLight: '#3D6B5A',
    border: '#1E3D32',
    text: '#F5F0E1',
    textMuted: '#C9CDB8',
    accent: '#9CAF88',
    stripe: '#234A3D',
  },
  // Navy Blue - Orient Express
  {
    background: '#2C3E5C',
    backgroundLight: '#3D4F6D',
    border: '#1B2840',
    text: '#F5EED9',
    textMuted: '#B8C5D4',
    accent: '#C9A962',
    stripe: '#243350',
  },
  // Burnt Orange - Southern Pacific
  {
    background: '#B5562A',
    backgroundLight: '#C66A3D',
    border: '#8B3D1A',
    text: '#FFF9F0',
    textMuted: '#F5DCC8',
    accent: '#F4D03F',
    stripe: '#9A4722',
  },
  // Deep Teal - Canadian Pacific
  {
    background: '#1D6B6B',
    backgroundLight: '#2A7D7D',
    border: '#134949',
    text: '#F0F5F5',
    textMuted: '#B8D4D4',
    accent: '#E8C547',
    stripe: '#175858',
  },
  // Mustard Yellow - Union Pacific
  {
    background: '#C4982E',
    backgroundLight: '#D4A83E',
    border: '#9A7420',
    text: '#2B1810',
    textMuted: '#4A3828',
    accent: '#8B4513',
    stripe: '#A88025',
  },
  // Dusty Rose - Victorian era
  {
    background: '#9E6B6B',
    backgroundLight: '#B07D7D',
    border: '#7A4F4F',
    text: '#FFF8F5',
    textMuted: '#E8D5D0',
    accent: '#D4A088',
    stripe: '#8A5858',
  },
  // Slate Blue - Pennsylvania Railroad
  {
    background: '#4A5568',
    backgroundLight: '#5A657A',
    border: '#2D3748',
    text: '#F7FAFC',
    textMuted: '#CBD5E0',
    accent: '#C5A572',
    stripe: '#3D4A5C',
  },
]

/**
 * Generate vintage-appropriate color palette for train tickets
 * Returns colors inspired by old railway tickets from different eras and companies
 */
export function getTicketColors(id: string) {
  const hash = hashStringToNumber(id)
  const palette = vintageTicketPalettes[hash % vintageTicketPalettes.length]

  return {
    // Main gradient (card background)
    gradientFrom: palette.background,
    gradientTo: palette.backgroundLight,

    // Border color
    border: palette.border,

    // Perforated edge color
    perforatedFrom: palette.border,

    // Text colors
    text: palette.text,
    textMuted: palette.textMuted,

    // Accent color (for arrows, decorative elements)
    accent: palette.accent,

    // Bottom stripe
    stripeFrom: palette.stripe,
    stripeVia: palette.accent,
    stripeTo: palette.stripe,

    // Decorative stamp
    stamp: palette.textMuted,
  }
}

/**
 * Get ticket colors with inline CSS variables for easy styling
 */
export function getTicketColorVars(id: string) {
  const colors = getTicketColors(id)

  return {
    '--ticket-gradient-from': colors.gradientFrom,
    '--ticket-gradient-to': colors.gradientTo,
    '--ticket-border': colors.border,
    '--ticket-perforated': colors.perforatedFrom,
    '--ticket-text': colors.text,
    '--ticket-muted': colors.textMuted,
    '--ticket-accent': colors.accent,
    '--ticket-stripe-from': colors.stripeFrom,
    '--ticket-stripe-via': colors.stripeVia,
    '--ticket-stripe-to': colors.stripeTo,
    '--ticket-stamp': colors.stamp,
  } as React.CSSProperties
}

/**
 * Passport color palettes inspired by real passports from around the world
 * Each palette represents a different country's passport aesthetic
 */
const passportPalettes = [
  // Navy Blue - USA, UK, Australia
  {
    cover: '#1a2e4a',
    coverLight: '#243d5f',
    coverDark: '#0f1c2e',
    gold: '#c9a962',
    goldLight: '#dfc57a',
    text: '#f5eed9',
    textMuted: '#b8c5d4',
    pages: '#f5f0e6',
    stamp: '#8b4513',
  },
  // Burgundy/Wine Red - EU, China
  {
    cover: '#6b1a2e',
    coverLight: '#8b2942',
    coverDark: '#4a1220',
    gold: '#d4a574',
    goldLight: '#e8c094',
    text: '#fff8e7',
    textMuted: '#e8d5c4',
    pages: '#faf5eb',
    stamp: '#1a4a3a',
  },
  // Dark Green - Ireland, Mexico, many African nations
  {
    cover: '#1e4a3a',
    coverLight: '#2a5a4a',
    coverDark: '#142e26',
    gold: '#c9a962',
    goldLight: '#dfc57a',
    text: '#f5f0e1',
    textMuted: '#c9cdb8',
    pages: '#f5f2e8',
    stamp: '#6b2237',
  },
  // Black - New Zealand, some Caribbean
  {
    cover: '#1a1a1a',
    coverLight: '#2d2d2d',
    coverDark: '#0d0d0d',
    gold: '#c5a572',
    goldLight: '#d9bb8a',
    text: '#f7fafc',
    textMuted: '#cbd5e0',
    pages: '#fafaf8',
    stamp: '#4a2c1a',
  },
  // Maroon - Switzerland, Singapore
  {
    cover: '#5c1a2d',
    coverLight: '#7a2840',
    coverDark: '#3d1020',
    gold: '#e8c547',
    goldLight: '#f0d668',
    text: '#fff9f0',
    textMuted: '#f5dcc8',
    pages: '#faf6f0',
    stamp: '#2c5a4a',
  },
  // Royal Blue - Caribbean nations
  {
    cover: '#1e3a6e',
    coverLight: '#2a4a82',
    coverDark: '#142a50',
    gold: '#f4d03f',
    goldLight: '#f8dc68',
    text: '#f5f8ff',
    textMuted: '#b8c8e0',
    pages: '#f8f8f5',
    stamp: '#8b2942',
  },
  // Olive Green - Middle Eastern countries
  {
    cover: '#4a5a3a',
    coverLight: '#5a6a4a',
    coverDark: '#3a4a2a',
    gold: '#d4a574',
    goldLight: '#e8c094',
    text: '#f5f5e8',
    textMuted: '#d0d4c0',
    pages: '#f5f3eb',
    stamp: '#5c2a4a',
  },
  // Crimson Red - South Korea
  {
    cover: '#a02040',
    coverLight: '#b83050',
    coverDark: '#801830',
    gold: '#c9a962',
    goldLight: '#dfc57a',
    text: '#fff8f5',
    textMuted: '#f0d8d0',
    pages: '#faf5f2',
    stamp: '#1a3a5a',
  },
]

/**
 * Travel stamp colors for passport decorations
 */
const stampColors = [
  '#8b4513', // Brown
  '#1a4a3a', // Dark green
  '#6b2237', // Burgundy
  '#2c3e5c', // Navy
  '#4a2c1a', // Dark brown
  '#5c1a2d', // Maroon
  '#1e3a6e', // Royal blue
  '#b5562a', // Orange
]

/**
 * Generate passport-appropriate color palette
 * Returns colors inspired by real passports from around the world
 */
export function getPassportColors(id: string) {
  const hash = hashStringToNumber(id)
  const palette = passportPalettes[hash % passportPalettes.length]

  return {
    // Cover colors
    cover: palette.cover,
    coverLight: palette.coverLight,
    coverDark: palette.coverDark,

    // Gold/metallic accents (embossing)
    gold: palette.gold,
    goldLight: palette.goldLight,

    // Text colors
    text: palette.text,
    textMuted: palette.textMuted,

    // Interior page color
    pages: palette.pages,

    // Default stamp color
    stamp: palette.stamp,
  }
}

/**
 * Get random stamp colors for a passport based on trip ID
 * Returns 2-4 stamp colors for variety
 */
export function getPassportStamps(id: string) {
  const hash = hashStringToNumber(id)
  const numStamps = 2 + (hash % 3) // 2-4 stamps

  const stamps = []
  for (let i = 0; i < numStamps; i++) {
    const colorIndex = (hash + i * 7) % stampColors.length
    stamps.push({
      color: stampColors[colorIndex],
      rotation: ((hash + i * 13) % 40) - 20, // -20 to 20 degrees
      x: 10 + ((hash + i * 17) % 30), // 10-40% from left
      y: 15 + ((hash + i * 23) % 50), // 15-65% from top
      scale: 0.8 + ((hash + i * 11) % 5) / 10, // 0.8-1.2 scale
    })
  }

  return stamps
}
